import { DateRange, DateRangeID, Flags } from '@fullstory/client-api';
import { TimeRange } from '@fullstory/proto/fs/api/segment';
import {
  BadClickType,
  CustomEvent,
  Opportunity,
  Signal,
  SignalType,
} from '@fullstory/proto/fs/services/alexa';
import {
  ImpactsTableConfig,
  OpportunitySnapshot,
} from '@fullstory/proto/fs/services/webber/internal/impacts';
import { resetDateToMidnight } from '@fullstory/util/date';
import { WithID } from '@fullstory/util/uid';
import moment from 'moment';
import { shortOrCommaNumber } from '@fullstory/util/fmt';
import {
  ConversionsDateRangeIdToDateRangeID,
  InputBadClickTypes,
  OpportunitiesWithID,
  SignalTypeNames,
} from './types';
import { dateRangeById } from '@fullstory/search-model';
import { DateRangeSetting } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { int64, Timestamp } from '@fullstory/proto/google/protobuf';
import { sortBy } from 'lodash';
import { MAX_CONVERSION_ANALYSIS_DAYS } from './constants';
import { addDays } from 'date-fns';

export const opportunityFromSnapshot = (snapshot: OpportunitySnapshot): WithID<Opportunity> => ({
  id: snapshot.id,
  label: snapshot.label,
  steps: snapshot.steps,
  impactStats: snapshot.impactStats,
  impactScope: snapshot.impactScope,
  signal: snapshot.signal,
  computedAt: snapshot.createdAt,
});

export const dateRangeToTimeRange = (dateRange: DateRange): TimeRange => {
  let end = dateRange.range.End;
  // Set the custom end date to include the full day selected, or to now-30 minutes for today
  if (dateRange.id === DateRangeID.Custom) {
    const now = new Date();
    const endDate = new Date(end);
    if (endDate.getTime() === resetDateToMidnight(now).getTime()) {
      now.setMinutes(now.getMinutes() - 30);
      end = now.toISOString();
    } else {
      end = moment(endDate).add(1, 'day').toISOString();
    }
  }
  return {
    startTime: dateRange.range.Start,
    endTime: end,
  };
};

/**
 * Validates the given config to ensure the date range is still valid. If the 'Since' date range (if applicable) isn't
 * valid anymore, then it will be reset to "Since 31 days ago" and return an updated config.
 */
export const validateAndAdjustConfig = (
  currentSavedConfig: ImpactsTableConfig,
): ImpactsTableConfig => {
  if (currentSavedConfig?.dateRangeId === DateRangeID.Since) {
    const startTimeDate = Timestamp.toDate(currentSavedConfig.startTime);
    const adjustedStartTime = adjustSinceDateIfNeeded(startTimeDate);
    if (adjustedStartTime.getTime() === startTimeDate?.getTime()) {
      return currentSavedConfig;
    } else {
      return {
        ...currentSavedConfig,
        startTime: Timestamp.fromDate(adjustedStartTime),
      };
    }
  }
  return currentSavedConfig;
};

/**
 * Checks if the given start date is over 31 days from now. If it is, then a new valid start date is returned. This
 * function should only be called if the 'Since' time period option is being used.
 */
export const adjustSinceDateIfNeeded = (startDate: Date | undefined | null) => {
  const earliestValidDate = resetDateToMidnight(addDays(new Date(), -MAX_CONVERSION_ANALYSIS_DAYS));

  if (!startDate || startDate < earliestValidDate) {
    return earliestValidDate;
  }

  return startDate;
};

/**
 * Displays the projected conversions in a readable format
 */
export function projectAndFormatConversionsCount(multiplier: number, conversions: number): string {
  return shortOrCommaNumber(Math.round(multiplier * conversions), { decimalPlaces: 1 });
}

export interface FormattedConversionFunnelData {
  conversionRate: string;
  medianTimeFromFirst: string;
}

export const dateRangeSettingToTimeRange = (dateRange: DateRangeSetting) => {
  const dateRangeID = ConversionsDateRangeIdToDateRangeID[dateRange.dateRangeId];
  if (
    dateRangeID &&
    dateRangeID != DateRangeID.Since &&
    dateRangeID != DateRangeID.Custom &&
    dateRangeById[dateRangeID]
  ) {
    const range = dateRangeById[dateRangeID].range;
    return {
      startTime: range.Start,
      endTime: range.End,
    };
  } else {
    return {
      startTime: dateRange.startTime as string,
      endTime: dateRange.endTime as string,
    };
  }
};

export const signalTypeName = (signal: Signal, flags: Flags) => {
  switch (signal.type) {
    case SignalType.SIGNAL_TYPE_BAD_CLICK:
      return InputBadClickTypes[signal.clickType ?? BadClickType.CLICK_TYPE_INVALID];
    default:
      return SignalTypeNames[signal.type];
  }
};

/**
 *
 * @param multiplier the multiple of the projection range - if projecting a 2 day time range over a year, the multiplier would be 365/2 = 182.5
 * @param averageOrderValue the average order value of users in a revenue funnel
 * @param conversions the number of successful conversions in a funnel
 *
 * Since we display the average order value to the hundredths, e.g. 7.12, do the calculation using the display value
 * rather than the real value
 */
export function projectRevenueAttribution(
  multiplier: number | null,
  averageOrderValue: number | null,
  conversions: number | null,
): number | null {
  if (multiplier == null || averageOrderValue == null || conversions == null) return null;
  if (!isFinite(multiplier) || !isFinite(averageOrderValue) || !isFinite(conversions)) return null;
  return multiplier * parseFloat(averageOrderValue.toFixed(2)) * conversions;
}

const getCustomEventKey = (event: CustomEvent): string =>
  // sort by property name for a consistent key
  `${event.name}-${sortBy(event.properties, 'propertyName')
    .map(p => p.propertyName)
    .join()}`;

/**
 *
 * @param opportunities The list of opportunities to filter
 * @param currentConfig The current analysis configuration
 * @returns a list of opportunities that only include events that have been configured
 */
export const filterOpportunitiesBySignalCandidates = (
  opportunities: OpportunitiesWithID[],
  currentConfig: Pick<ImpactsTableConfig, 'customEvents' | 'definedEventIds'>,
): OpportunitiesWithID[] => {
  const eventSignalsToKeepTable: { [key: string]: boolean } = {};
  currentConfig.customEvents.forEach(ce => {
    // properties for custom event signals should only contain 1 element
    eventSignalsToKeepTable[getCustomEventKey(ce)] = true;
  });
  currentConfig.definedEventIds.forEach(id => {
    eventSignalsToKeepTable[id] = true;
  });

  return opportunities.filter(d => {
    const isConfident = d.impactStats?.confidence && d.impactStats?.confidence >= 1;
    const hasConversionsLost =
      d.impactStats?.conversionsLost && int64.toNumber(d.impactStats.conversionsLost);
    let shouldKeepSignal = false;
    switch (d.signal?.type) {
      case SignalType.SIGNAL_TYPE_CUSTOM_EVENT:
        if (
          d.signal?.customevent &&
          eventSignalsToKeepTable[getCustomEventKey(d.signal.customevent)]
        ) {
          shouldKeepSignal = true;
        }
        break;
      case SignalType.SIGNAL_TYPE_DEFINED_EVENT:
        if (d.signal?.definedEventId && eventSignalsToKeepTable[d.signal.definedEventId]) {
          shouldKeepSignal = true;
        }
        break;
      default:
        shouldKeepSignal = true;
    }
    return isConfident && hasConversionsLost && shouldKeepSignal;
  });
};
